export type SeasonEventDispositionMetric =
  | 'Cartons'
  | 'Inventory $'
  | 'Sellable Units'

export const METRIC_OPTIONS: SeasonEventDispositionMetric[] = [
  'Cartons',
  'Inventory $',
  'Sellable Units',
]

export const IMP_DOM_OPTIONS = ['Import', 'Domestic']

export const DC_OPTIONS = [
  '1_Dallas',
  '2_Atlanta',
  '3_Memphis',
  '4_Indianapolis',
  '5_Los Angeles',
]

interface LeafDef {
  key: string
  label: string
  groupKey: string
  /** Baseline "Sellable Units" value — anchored to the reference screenshot. */
  baseUnits: number
}

interface GroupDef {
  key: string
  label: string
  leafKeys: string[]
}

// Baseline leaf values (Sellable Units, all-filters-default) anchored to the
// reference screenshot. Group subtotals and the grand Total are always
// *derived* from these (never hardcoded) so they stay internally consistent
// under filter-driven shrink.
const LEAF_DEFS: LeafDef[] = [
  { key: 'cogs', label: 'COGS', groupKey: 'cogs', baseUnits: 59440017 },
  {
    key: 'inStore',
    label: 'In Store',
    groupKey: 'mikSupplyChain',
    baseUnits: 34633390,
  },
  {
    key: 'inTransitToStore',
    label: 'In Transit to Store',
    groupKey: 'mikSupplyChain',
    baseUnits: 39880113,
  },
  {
    key: 'inDC',
    label: 'In DC',
    groupKey: 'mikSupplyChain',
    baseUnits: 6491076,
  },
  {
    key: 'dcYard',
    label: 'DC Yard',
    groupKey: 'stateside',
    baseUnits: 2833392,
  },
  {
    key: 'inTransitImport',
    label: 'In Transit Import',
    groupKey: 'stateside',
    baseUnits: 37138,
  },
  { key: 'usPort', label: 'US Port', groupKey: 'stateside', baseUnits: 48838 },
  {
    key: 'onWater',
    label: 'On Water',
    groupKey: 'overseas',
    baseUnits: 9224779,
  },
  {
    key: 'originPort',
    label: 'Origin Port',
    groupKey: 'overseas',
    baseUnits: 664475,
  },
  {
    key: 'vendorDomestic',
    label: 'Vendor Domestic',
    groupKey: 'vendor',
    baseUnits: 5596781,
  },
  {
    key: 'vendorImport',
    label: 'Vendor Import',
    groupKey: 'vendor',
    baseUnits: 65088154,
  },
]

const GROUP_DEFS: GroupDef[] = [
  { key: 'cogs', label: 'COGS', leafKeys: ['cogs'] },
  {
    key: 'mikSupplyChain',
    label: 'Company Supply Chain',
    leafKeys: ['inStore', 'inTransitToStore', 'inDC'],
  },
  {
    key: 'stateside',
    label: 'Stateside',
    leafKeys: ['dcYard', 'inTransitImport', 'usPort'],
  },
  {
    key: 'overseas',
    label: 'Overseas',
    leafKeys: ['onWater', 'originPort'],
  },
  {
    key: 'vendor',
    label: 'Vendor',
    leafKeys: ['vendorDomestic', 'vendorImport'],
  },
]

// No reference screenshot exists for the "Cartons" / "Inventory $" metric
// states. These scale factors are a documented assumption derived off the
// Sellable Units baseline (~14 units/carton, ~$4.20 average unit cost) so the
// relative proportions across supply-chain stages are preserved and the
// Total/% math still reconciles. Replace with real anchors if screenshots
// for those two metric states become available.
const CARTONS_PER_UNIT = 1 / 14
const INVENTORY_DOLLARS_PER_UNIT = 4.2

function baseValueForMetric(
  baseUnits: number,
  metric: SeasonEventDispositionMetric
): number {
  switch (metric) {
    case 'Cartons':
      return Math.round(baseUnits * CARTONS_PER_UNIT)
    case 'Inventory $':
      return Math.round(baseUnits * INVENTORY_DOLLARS_PER_UNIT)
    case 'Sellable Units':
      return baseUnits
  }
}

export interface SeasonEventDispositionFilterState {
  season: string | null
  division: string | null
  departmentId: string | null
  classId: string | null
  subclassId: string | null
  impDom: string | null
  dc: string | null
  snapshotBase: string
  metric: SeasonEventDispositionMetric
}

function hashString(input: string): number {
  let h = 2166136261
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

function normalizedFromHash(seed: number, salt: string): number {
  return (hashString(`${seed}:${salt}`) % 10000) / 10000
}

function activeShrinkFilterPairs(
  filters: SeasonEventDispositionFilterState
): Array<[string, string]> {
  const pairs: Array<[string, string]> = []
  if (filters.season) pairs.push(['season', filters.season])
  if (filters.division) pairs.push(['division', filters.division])
  if (filters.departmentId) pairs.push(['dept', filters.departmentId])
  if (filters.classId) pairs.push(['class', filters.classId])
  if (filters.subclassId) pairs.push(['subclass', filters.subclassId])
  if (filters.impDom) pairs.push(['impDom', filters.impDom])
  if (filters.dc) pairs.push(['dc', filters.dc])
  return pairs
}

function shrinkForLeafFilter(
  leafKey: string,
  filterName: string,
  filterValue: string
): number {
  // Each active filter narrows a leaf's value to roughly 40%-75% of its
  // unfiltered baseline, seeded per-leaf so different stages shrink
  // independently rather than moving in lockstep.
  const seed = hashString(`${leafKey}:${filterValue}`)
  const n = normalizedFromHash(seed, filterName)
  return 0.4 + n * 0.35
}

export interface SeasonEventDispositionRow {
  key: string
  label: string
  metricBase: number
  metricPctOfTotalBase: number
}

export interface SeasonEventDispositionGroup {
  key: string
  label: string
  metricBase: number
  metricPctOfTotalBase: number
  leaves: SeasonEventDispositionRow[]
}

export interface SeasonEventDispositionResult {
  groups: SeasonEventDispositionGroup[]
  total: SeasonEventDispositionRow
  whatYouNeedToKnow: string
}

export function buildSeasonEventDispositionData(
  filters: SeasonEventDispositionFilterState
): SeasonEventDispositionResult {
  const activePairs = activeShrinkFilterPairs(filters)

  // Snapshot Base (when not "Latest") is a cosmetic-only wiggle — it does not
  // represent a narrower slice of data the way the other filters do, so it
  // nudges each leaf value by a small +-4% instead of compounding a shrink.
  // Percentages are always re-derived from the wiggled totals afterward, so
  // every displayed number stays internally consistent.
  const snapshotWiggleSeed =
    filters.snapshotBase && filters.snapshotBase !== 'Latest'
      ? hashString(`snapshot:${filters.snapshotBase}`)
      : 0

  const leafValues: Record<string, number> = {}
  for (const leaf of LEAF_DEFS) {
    let value = baseValueForMetric(leaf.baseUnits, filters.metric)

    const totalShrink = activePairs.reduce(
      (acc, [name, value2]) =>
        acc * shrinkForLeafFilter(leaf.key, name, value2),
      1
    )
    value *= totalShrink

    if (snapshotWiggleSeed !== 0) {
      const noise =
        (normalizedFromHash(snapshotWiggleSeed, leaf.key) - 0.5) * 0.08 // +-4%
      value *= 1 + noise
    }

    leafValues[leaf.key] = value
  }

  const grandTotal = LEAF_DEFS.reduce(
    (sum, leaf) => sum + leafValues[leaf.key],
    0
  )

  const pctOfTotal = (value: number): number =>
    grandTotal === 0 ? 0 : value / grandTotal

  const groups: SeasonEventDispositionGroup[] = GROUP_DEFS.map(groupDef => {
    const leaves: SeasonEventDispositionRow[] = groupDef.leafKeys.map(
      leafKey => {
        const leafDef = LEAF_DEFS.find(l => l.key === leafKey)!
        const value = leafValues[leafKey]
        return {
          key: leafDef.key,
          label: leafDef.label,
          metricBase: value,
          metricPctOfTotalBase: pctOfTotal(value),
        }
      }
    )
    const groupValue = leaves.reduce((sum, l) => sum + l.metricBase, 0)
    return {
      key: groupDef.key,
      label: groupDef.label,
      metricBase: groupValue,
      metricPctOfTotalBase: pctOfTotal(groupValue),
      leaves,
    }
  })

  const total: SeasonEventDispositionRow = {
    key: 'total',
    label: 'Total',
    metricBase: grandTotal,
    metricPctOfTotalBase: grandTotal === 0 ? 0 : 1,
  }

  const whatYouNeedToKnow = buildWhatYouNeedToKnow(groups)

  return { groups, total, whatYouNeedToKnow }
}

// Curated subset of nodes from the table's own computed "% of Total Base"
// column — intentionally does not sum to 100% (mirrors the reference
// screenshot, which also omits "In Transit to Store" from this narrative).
function buildWhatYouNeedToKnow(groups: SeasonEventDispositionGroup[]): string {
  const findGroup = (key: string) => groups.find(g => g.key === key)!
  const findLeaf = (groupKey: string, leafKey: string) =>
    findGroup(groupKey).leaves.find(l => l.key === leafKey)!

  const pct = (fraction: number) => Math.round(fraction * 100)

  const vendorPct = pct(findGroup('vendor').metricPctOfTotalBase)
  const originPortPct = pct(
    findLeaf('overseas', 'originPort').metricPctOfTotalBase
  )
  const onWaterPct = pct(findLeaf('overseas', 'onWater').metricPctOfTotalBase)
  const statesidePct = pct(findGroup('stateside').metricPctOfTotalBase)
  const inDcPct = pct(findLeaf('mikSupplyChain', 'inDC').metricPctOfTotalBase)
  const inStorePct = pct(
    findLeaf('mikSupplyChain', 'inStore').metricPctOfTotalBase
  )
  const cogsPct = pct(findGroup('cogs').metricPctOfTotalBase)

  return [
    `${vendorPct}% at vendor`,
    `${originPortPct}% origin ports`,
    `${onWaterPct}% on water`,
    `${statesidePct}% stateside`,
    `${inDcPct}% in DC`,
    `${inStorePct}% in-store`,
    `${cogsPct}% sold`,
  ].join(' | ')
}

/** Generates a synthetic weekly snapshot id list in YYYYMMDD format, going
 * backward from `referenceDate`. Used for both Snapshot Base and Compare To. */
export function generateSnapshotOptions(
  referenceDate: Date,
  weeksBack = 12
): string[] {
  const options: string[] = []
  for (let i = 1; i <= weeksBack; i++) {
    const d = new Date(referenceDate)
    d.setDate(d.getDate() - i * 7)
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    options.push(`${y}${m}${day}`)
  }
  return options
}
